import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Typography, Skeleton, Button, Chip } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import AppDialog from '../common/AppDialog';
import { TONE_META } from '../Dashboard/AiSummaryCard';
import { formatCurrency } from '../../utils/calculationUtils';
import { formatAxisCurrency } from '../../utils/chartAxisUtils';
import buildCategoryAnalysisPayload from '../../utils/categoryAnalysisPayload';
import api from '../../services/api';

const TREND_META = {
  increasing: { Icon: TrendingUpIcon, label: 'Increasing' },
  decreasing: { Icon: TrendingDownIcon, label: 'Decreasing' },
  stable: { Icon: TrendingFlatIcon, label: 'Stable' },
  volatile: { Icon: ShowChartIcon, label: 'Volatile' },
};

/*
  CategoryAnalysisDialog: Claude-written pattern analysis of one category —
  trend, seasonality, unusual months, observations — over its monthly history.
  Sends only client-computed aggregates (monthly series + stats + top
  recurring descriptions), never raw transactions. The backend caches per
  user + category on a hash of the payload, so reopening is free until the
  numbers change — and the card InsightsPanel builds the identical payload,
  so panel and dialog share that cache entry.
*/
const CategoryAnalysisDialog = ({ open, onClose, category, budgetData, transactions }) => {
  const [analysis, setAnalysis] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [cached, setCached] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  // Generations left in the user's global daily AI allowance (shared across
  // the whole app); null until the backend has said. Drives the Regenerate
  // button.
  const [remaining, setRemaining] = useState(null);
  // Ignore responses from superseded requests (rapid open/regenerate).
  const requestIdRef = useRef(0);

  // The category's monthly series, leading all-zero months trimmed. Built by
  // the shared helper so it hashes identically to the card panel's request.
  const payload = useMemo(
    () => buildCategoryAnalysisPayload(category, budgetData, transactions),
    [category, budgetData, transactions]
  );

  const noData = !payload || payload.noData;
  // Serialized once so the fetch effect keys off content, not object identity.
  const payloadJson = useMemo(() => (noData ? null : JSON.stringify(payload)), [payload, noData]);

  const fetchAnalysis = useCallback(
    async (force) => {
      if (!category || !payloadJson) return;
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const { data: res } = await api.post('/ai/category-analysis', {
          categoryId: category.id,
          data: JSON.parse(payloadJson),
          force,
        });
        if (requestId !== requestIdRef.current) return;
        setAnalysis(res.analysis);
        setGeneratedAt(res.generatedAt);
        setCached(Boolean(res.cached));
        if (typeof res.aiCredits?.remaining === 'number') setRemaining(res.aiCredits.remaining);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        if (err.response?.status === 503) {
          setUnavailable(true);
        } else {
          if (typeof err.response?.data?.aiCredits?.remaining === 'number') {
            setRemaining(err.response.data.aiCredits.remaining);
          }
          setError(err.response?.data?.error || 'Could not analyze this category right now.');
        }
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [category, payloadJson]
  );

  // Fresh state per category, fetch on open. Unlike the Overview summary this
  // is an explicit user action, so no debounce.
  useEffect(() => {
    if (!open) return;
    setAnalysis(null);
    setGeneratedAt(null);
    setCached(false);
    setError(null);
    setUnavailable(false);
    if (payloadJson) fetchAnalysis(false);
  }, [open, payloadJson, fetchAnalysis]);

  const coveredRange = !noData && payload.months.length
    ? `${payload.months[0].month} to ${payload.months[payload.months.length - 1].month}`
    : 'No activity yet';

  const trendMeta = analysis ? TREND_META[analysis.trend] : null;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={`AI Analysis · ${category?.name || ''}`}
      subtitle={coveredRange}
      maxWidth="sm"
      primaryLabel="Regenerate"
      onPrimary={() => fetchAnalysis(true)}
      primaryDisabled={loading || noData || unavailable || remaining === 0}
      secondaryLabel="Close"
    >
      {noData ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No transaction data for this category yet.
        </Typography>
      ) : (
        <>
          {/* Context chart renders immediately, even while the AI call runs */}
          <Box sx={{ mt: 2, height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={payload.months} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  width={44}
                  tickFormatter={formatAxisCurrency}
                />
                <RechartsTooltip
                  formatter={(value) => [formatCurrency(value), payload.amountMeaning === 'income' ? 'Received' : payload.amountMeaning === 'net' ? 'Net' : 'Spent']}
                  labelStyle={{ fontSize: 13 }}
                  contentStyle={{ fontSize: 13, borderRadius: 8 }}
                />
                <Bar dataKey="amount" fill={category?.color || '#3f51b5'} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>

          {unavailable ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              AI isn't configured on the server (ANTHROPIC_API_KEY is not set).
            </Typography>
          ) : loading && !analysis ? (
            <Box sx={{ mt: 2 }}>
              <Skeleton width="55%" height={22} />
              <Skeleton width="90%" height={18} />
              <Skeleton width="75%" height={18} />
              <Skeleton width="82%" height={18} />
            </Box>
          ) : error && !analysis ? (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="body3" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                {error}
              </Typography>
              <Button size="small" onClick={() => fetchAnalysis(false)}>
                Try again
              </Button>
            </Box>
          ) : analysis ? (
            // Dim (rather than blank) while a regenerate is in flight
            <Box sx={{ mt: 2, opacity: loading ? 0.5 : 1, transition: 'opacity 150ms ease' }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                {analysis.headline}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                {trendMeta && (
                  <Chip
                    size="small"
                    icon={<trendMeta.Icon sx={{ fontSize: '1rem' }} />}
                    label={trendMeta.label}
                    sx={{ height: 22, fontSize: '0.75rem' }}
                  />
                )}
                {analysis.seasonality && (
                  <Typography variant="caption" color="text.secondary">
                    {analysis.seasonality}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {(analysis.insights || []).map((insight, index) => {
                  const { Icon, color } = TONE_META[insight.tone] || TONE_META.info;
                  return (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Icon sx={{ fontSize: '1rem', color, mt: '2px', flexShrink: 0 }} />
                      <Typography variant="body3" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        {insight.text}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              {(analysis.unusualMonths || []).length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                    Unusual months
                  </Typography>
                  {analysis.unusualMonths.map((um, index) => (
                    <Typography key={index} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {um.month}: {um.reason}
                    </Typography>
                  ))}
                </Box>
              )}
              {generatedAt && (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
                  Generated {new Date(generatedAt).toLocaleString()}
                  {cached ? ' (cached)' : ''}
                </Typography>
              )}
              {error && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  {error}
                </Typography>
              )}
            </Box>
          ) : null}
        </>
      )}
    </AppDialog>
  );
};

export default CategoryAnalysisDialog;
